import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';
import { FormField } from '@packages/ui/patterns/FormField';
import { Button } from '@packages/ui/primitives';
import { rhfInlineError } from '@packages/ui/hook-form-adapters/rhfInlineError';

export interface RhfSignatureProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
}

interface SignaturePadState {
  isDrawing: boolean;
}

type SignaturePadAction =
  | { type: 'START_DRAW' }
  | { type: 'STOP_DRAW' };

function signaturePadReducer(state: SignaturePadState, action: SignaturePadAction): SignaturePadState {
  switch (action.type) {
    case 'START_DRAW':
      return { ...state, isDrawing: true };
    case 'STOP_DRAW':
      return { ...state, isDrawing: false };
  }
}

const CANVAS_WIDTH = 400;
const CANVAS_HEIGHT = 120;

interface SignatureCanvasProps {
  value: unknown;
  disabled?: boolean;
  label: string;
  onChange: (value: string | null) => void;
}

function SignatureCanvas({ value, disabled, label, onChange }: SignatureCanvasProps): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [padState, dispatchPad] = useReducer(signaturePadReducer, { isDrawing: false });

  const initCanvas = useCallback((ctx: CanvasRenderingContext2D) => {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const loadDataUrl = useCallback(
    (dataUrl: string | null) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      initCanvas(ctx);
      if (!dataUrl) return;

      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      };
      img.src = dataUrl;
    },
    [initCanvas],
  );

  useEffect(() => {
    loadDataUrl(typeof value === 'string' ? value : null);
  }, [value, loadDataUrl]);

  const getCoords = (event: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in event) {
      const touch = event.touches[0] ?? event.changedTouches[0];
      if (!touch) return null;
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    }

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (event: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const coords = getCoords(event);
    if (!ctx || !coords) return;
    dispatchPad({ type: 'START_DRAW' });
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (event: React.MouseEvent | React.TouchEvent) => {
    if (disabled || !padState.isDrawing) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const coords = getCoords(event);
    if (!ctx || !coords) return;
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const endDraw = () => {
    if (!padState.isDrawing) return;
    dispatchPad({ type: 'STOP_DRAW' });
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange(canvas.toDataURL('image/png'));
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    initCanvas(ctx);
    onChange(null);
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        aria-label={label}
        style={{
          width: '100%',
          maxWidth: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          border: '1px solid var(--ui-border-default, #d1d5db)',
          borderRadius: 4,
          touchAction: 'none',
          cursor: disabled ? 'not-allowed' : 'crosshair',
          opacity: 1,
          backgroundColor: disabled ? 'var(--ui-background-muted, #F3F3F3)' : 'var(--ui-surface, #fff)',
        }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      {!disabled ? (
        <Button
          type='button'
          variant='secondary-gray'
          size='sm'
          label='Clear'
          onClick={clearSignature}
          style={{ marginTop: 8 }}
        />
      ) : null}
    </>
  );
}

export const RhfSignature = React.forwardRef<HTMLCanvasElement, RhfSignatureProps>(
  function RhfSignature(
    { control, name, label, hint, required = false, semanticId, disabled },
  ): React.ReactElement {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });

          return (
            <FormField
              label={label}
              hint={hint}
              required={required}
              semanticId={semanticId ?? name}
              disabled={disabled}
              error={errorText}
            >
              <SignatureCanvas
                value={field.value}
                disabled={disabled}
                label={label}
                onChange={field.onChange}
              />
            </FormField>
          );
        }}
      />
    );
  },
);
